import { useCallback, useState } from 'react'
import constant from 'lodash/constant'
import get from 'lodash/get'
import isString from 'lodash/isString'
import { ToastAction } from '@klear/ui/components/toast'
import { toast } from '@klear/ui/hooks/use-toast'
import { redirectToLogin } from '@klear/ui/lib/auth-redirect'

export type FileDownloadOutcome = 'downloaded' | 'unauthenticated' | 'failed'

const GENERIC_FAILURE = 'Something went wrong while downloading. Please try again.'

async function serverErrorMessage(response: Response): Promise<string | null> {
  const body = await response.json().catch(constant(null))
  const message = get(body, 'error')
  return isString(message) && message.length > 0 ? message : null
}

function describeFailure(status: number, serverMessage: string | null): string {
  if (status === 403) {
    return 'You do not have access to this document.'
  }
  if (status === 404) {
    return 'This document is no longer available.'
  }
  if (status === 400 || status === 409) {
    return serverMessage ?? GENERIC_FAILURE
  }
  return GENERIC_FAILURE
}

function isRetryable(status: number | null): boolean {
  return status === null || status >= 500
}

function failureToast(description: string, url: string, filename: string | undefined, status: number | null): void {
  toast({
    variant: 'destructive',
    title: 'Download failed',
    description,
    action: isRetryable(status) ? (
      <ToastAction altText="Retry download" onClick={() => downloadFile(url, filename)}>
        Retry
      </ToastAction>
    ) : undefined
  })
}

function filenameFromResponse(response: Response): string | null {
  const header = response.headers.get('Content-Disposition')
  if (header === null) {
    return null
  }

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (encoded?.[1] !== undefined) {
    try {
      return decodeURIComponent(encoded[1])
    } catch {}
  }

  const quoted = /filename="((?:[^"\\]|\\.)*)"/i.exec(header)
  if (quoted?.[1] === undefined || quoted[1].length === 0) {
    return null
  }
  return quoted[1].replace(/\\(.)/g, '$1')
}

function filenameFromUrl(url: string): string {
  const lastSegment = (url.split('?')[0] ?? '').split('/').pop() ?? ''
  return lastSegment.length > 0 ? decodeURIComponent(lastSegment) : 'download'
}

function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
}

export async function downloadFile(url: string, filename?: string): Promise<FileDownloadOutcome> {
  let response: Response
  try {
    response = await fetch(url, { credentials: 'include' })
  } catch {
    failureToast(GENERIC_FAILURE, url, filename, null)
    return 'failed'
  }

  if (response.status === 401) {
    redirectToLogin()
    return 'unauthenticated'
  }

  if (!response.ok) {
    failureToast(describeFailure(response.status, await serverErrorMessage(response)), url, filename, response.status)
    return 'failed'
  }

  const serverFilename = filenameFromResponse(response)
  try {
    saveBlob(await response.blob(), serverFilename ?? filename ?? filenameFromUrl(url))
  } catch {
    failureToast(GENERIC_FAILURE, url, filename, null)
    return 'failed'
  }

  return 'downloaded'
}

export function useFileDownload() {
  const [isDownloading, setIsDownloading] = useState(false)

  const startDownload = useCallback(async (url: string, filename?: string): Promise<FileDownloadOutcome> => {
    setIsDownloading(true)
    try {
      return await downloadFile(url, filename)
    } finally {
      setIsDownloading(false)
    }
  }, [])

  return { startDownload, isDownloading }
}
